/**
 * Collection Image Fallback (Universal)
 * Handles image fallback for variant SKUs that don't have images on collection pages
 * Supports: Catalog Collection, Normal Collection, and Search pages
 * Falls back to base product or sibling variant images using Shopify API
 * 
 * Performance Optimizations:
 * - Caching: Stores fetched products to avoid duplicate API calls
 * - Reduced attempts: 2 handle patterns + 2 SKU search results = max 4 API calls per SKU
 * - SKU search priority: Most successful matches come from search, not handle patterns
 */

(function() {
    'use strict';
    
    // Cache to store fetched product data and avoid duplicate API calls
    var productCache = {};
    
    // Track statistics for debugging (optional)
    var stats = {
        processed: 0,
        successful: 0,
        failed: 0
    };
    
    // Function to check if image is a placeholder
    function isPlaceholderImage(imgSrc) {
        if (!imgSrc) return true;
        var imgSrcLower = imgSrc.toLowerCase();
        return imgSrcLower.includes('dnj_placeholder') || 
               imgSrcLower.includes('image_not_found') ||
               imgSrcLower.includes('placeholder');
    }
    
    // Function to get base SKU from variant SKU
    function getBaseSKU(variantSKU) {
        if (!variantSKU) return null;
        // Remove decimal part (e.g., "P4440.20" -> "P4440", "RB4322.10" -> "RB4322")
        return variantSKU.replace(/\.\d+$/, '');
    }
    
    // Function to check if SKU is a variant (has decimal part)
    function isVariantSKU(sku) {
        if (!sku) return false;
        return /\.\d+$/.test(sku);
    }
    
    // Function to detect product type from SKU prefix
    function getProductTypeFromSKU(sku) {
        if (!sku) return 'product';
        var skuLower = sku.toLowerCase();
        
        // Check 3-character prefixes first
        var threeCharPrefix = skuLower.substring(0, 3);
        var threeCharMap = {
            'fgs': 'full-gasket-set',
            'ek2': 'engine-rebuild-kit'
        };
        if (threeCharMap[threeCharPrefix]) {
            return threeCharMap[threeCharPrefix];
        }
        
        // Check 2-character prefixes
        var prefix = skuLower.substring(0, 2);
        var productTypeMap = {
            'p4': 'piston-set',
            'pr': 'piston-ring-set',
            'rb': 'rod-bearings-set',
            'mb': 'main-bearings-set',
            'op': 'oil-pump',
            'tb': 'timing-belt-kit',
            'tw': 'timing-chain-kit',
            'ek': 'engine-rebuild-kit',
            'cs': 'camshaft',
            'vk': 'valve-kit',
            'wp': 'water-pump'
        };
        return productTypeMap[prefix] || 'product';
    }
    
    // Function to check if product has images
    function hasProductImages(productData) {
        return productData && productData.images && productData.images.length > 0;
    }
    
    // Function to fetch product by handle (silently handles 404s)
    function fetchProductByHandle(handle, callback, errorCallback) {
        // Check cache first
        if (productCache[handle]) {
            callback(productCache[handle]);
            return;
        }
        
        // Check if we already tried and failed
        if (productCache[handle] === null) {
            if (errorCallback) errorCallback(new Error('Previously failed'));
            return;
        }
        
        var apiUrl = '/products/' + handle + '.js';
        
        // Use fetch with silent error handling to avoid console pollution
        fetch(apiUrl)
            .then(function(response) {
                // Silently handle 404s without throwing
                if (!response.ok) {
                    // Cache null for failed requests
                    productCache[handle] = null;
                    if (errorCallback) {
                        errorCallback(new Error('Product not found'));
                    }
                    return null;
                }
                return response.json();
            })
            .then(function(productData) {
                if (productData) {
                    // Cache the successful result
                    productCache[handle] = productData;
                    callback(productData);
                }
            })
            .catch(function(error) {
                // Silently catch network errors without console.error
                productCache[handle] = null;
                if (errorCallback) {
                    errorCallback(error);
                }
            });
    }
    
    // Function to search products by SKU using Shopify search API
    function searchProductsBySKU(sku, callback, errorCallback) {
        var searchUrl = '/search?type=product&q=' + encodeURIComponent(sku);
        
        fetch(searchUrl)
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Search failed');
                }
                return response.text();
            })
            .then(function(html) {
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, 'text/html');
                var handles = [];
                
                var productLinks = doc.querySelectorAll('a[href*="/products/"]');
                productLinks.forEach(function(link) {
                    var href = link.getAttribute('href');
                    if (href) {
                        var match = href.match(/\/products\/([^?#\/]+)/);
                        if (match && match[1]) {
                            var handle = match[1];
                            if (handles.indexOf(handle) === -1) {
                                handles.push(handle);
                            }
                        }
                    }
                });
                
                if (handles.length > 0) {
                    callback(handles);
                } else if (errorCallback) {
                    errorCallback();
                }
            })
            .catch(function(error) {
                if (errorCallback) {
                    errorCallback(error);
                }
            });
    }
    
    // Function to fetch base product and apply images to variant
    function fetchAndApplyBaseProductImages(variantProductItem, baseSKU, variantSKU, callback) {
        var productType = getProductTypeFromSKU(baseSKU);
        var baseSKULower = baseSKU.toLowerCase();
        var completed = false;
        
        function resolve(productData) {
            if (completed) return;
            completed = true;
            
            if (productData && hasProductImages(productData)) {
                applyImagesToVariant(variantProductItem, productData, variantSKU);
                if (callback) callback(true);
            } else {
                if (callback) callback(false);
            }
        }
        
        // Build handle attempts for base product (optimized to 2 most common patterns)
        // Most matches come from SKU search, so we try fewer handles first
        var attempts = [
            productType + '-' + baseSKULower,  // Most common pattern
            baseSKULower  // Direct SKU match
        ];
        
        var attemptIndex = 0;
        
        function tryFetch() {
            if (completed || attemptIndex >= attempts.length) {
                // All handle attempts exhausted, try SKU search
                trySKUSearch();
                return;
            }
            
            var handle = attempts[attemptIndex];
            attemptIndex++;
            
            fetchProductByHandle(handle, function(productData) {
                if (hasProductImages(productData)) {
                    resolve(productData);
                } else {
                    tryFetch();
                }
            }, function() {
                tryFetch();
            });
        }
        
        function trySKUSearch() {
            if (completed) return;
            
            searchProductsBySKU(baseSKU, function(handles) {
                var handleIndex = 0;
                
                function tryHandle() {
                    if (completed || handleIndex >= handles.length || handleIndex >= 2) {
                        if (callback) callback(false);
                        return;
                    }
                    
                    var handle = handles[handleIndex];
                    handleIndex++;
                    
                    fetchProductByHandle(handle, function(productData) {
                        if (hasProductImages(productData)) {
                            // Validate that this product is related to our base SKU
                            var isRelated = false;
                            if (productData.variants && productData.variants.length > 0) {
                                // Get base SKU without any letter suffixes for more flexible matching
                                var baseSKUCore = baseSKULower.replace(/[a-z]+$/i, ''); // MB4198W → MB4198
                                
                                for (var i = 0; i < productData.variants.length; i++) {
                                    var variantSKU = productData.variants[i].sku;
                                    if (variantSKU) {
                                        var variantSKULower = variantSKU.toLowerCase();
                                        var variantCore = variantSKULower.replace(/[a-z]*\..*$/i, '').replace(/[a-z]+$/i, '');
                                        
                                        // Check multiple matching patterns
                                        if (variantSKULower === baseSKULower || // Exact match
                                            variantSKULower.indexOf(baseSKULower) === 0 || // Starts with
                                            variantSKULower.indexOf(baseSKULower + '.') >= 0 || // Contains with dot
                                            variantCore === baseSKUCore || // Core number match
                                            (baseSKUCore && variantSKULower.indexOf(baseSKUCore) === 0)) { // Core starts with
                                            isRelated = true;
                                            // Variant matched
                                            break;
                                        }
                                    }
                                }
                            }
                            
                            if (isRelated || productData.variants.length === 1) {
                                // Product is related or has single variant (use it)
                                // Found related product
                                resolve(productData);
                            } else {
                                // Product not related, trying next
                                tryHandle();
                            }
                        } else {
                            tryHandle();
                        }
                    }, function() {
                        tryHandle();
                    });
                }
                
                tryHandle();
            }, function() {
                if (callback) callback(false);
            });
        }
        
        tryFetch();
    }
    
    // Function to apply product images to variant product element
    function applyImagesToVariant(variantProductItem, productData, variantSKU) {
        // Support catalog collection (list/grid), normal collection, and search pages
        var variantImageContainer = variantProductItem.querySelector('.product-list-image, .product-grid-image, .productitem--image');
        if (!variantImageContainer) return;
        
        // Try multiple selectors for the primary image
        var variantPrimaryImg = variantImageContainer.querySelector('.grid-main-image, .productitem--image-primary, img.no_img, img');
        if (!variantPrimaryImg) return;
        
        // Get the first image from the product
        if (productData.images && productData.images.length > 0) {
            var imageUrl = productData.images[0];
            
            // Update all image attributes
            variantPrimaryImg.setAttribute('data-srcset', imageUrl);
            variantPrimaryImg.setAttribute('srcset', imageUrl);
            variantPrimaryImg.src = imageUrl;
            
            // Remove the no_img class if it exists
            if (variantPrimaryImg.classList.contains('no_img')) {
                variantPrimaryImg.classList.remove('no_img');
            }
            
            // Also update noscript image if exists
            var noscriptImg = variantImageContainer.querySelector('noscript img');
            if (noscriptImg) {
                noscriptImg.setAttribute('src', imageUrl);
                noscriptImg.setAttribute('srcset', imageUrl);
            }
            
            // Track successful image application
            stats.successful++;
        }
    }
    
    // Function to fetch product SKU by Shopify product ID
    function fetchProductSKUByID(productId, callback, errorCallback) {
        // Use Shopify Admin API endpoint (publicly accessible product data)
        var apiUrl = '/admin/api/2023-10/products/' + productId + '.json';
        
        // Try alternative: Use product page scraping
        var graphqlUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'admin/products/' + productId : null;
        
        // Best approach: Fetch from cart add endpoint which returns product data
        var metaUrl = '/cart/add.js';
        
        // Fetching SKU for product
        
        // Since we don't have direct admin API access from frontend, 
        // we'll use the variant ID to fetch product data
        // The variant ID is available in the select element
        if (errorCallback) {
            errorCallback(new Error('Direct product ID fetch not supported from frontend'));
        }
    }
    
    // Function to get SKU from product element (sync - returns null if needs async fetch)
    function getSKUFromProductElementSync(productItem) {
        // First try to get from attr-prod-sku attribute (normal collection page)
        var skuAttr = productItem.getAttribute('attr-prod-sku');
        if (skuAttr && skuAttr.length > 0 && skuAttr !== 'undefined') {
            // Remove "_config" suffix if present
            return skuAttr.replace('_config', '').trim();
        }
        
        // Try to get from .productitem--sku .sku span (normal collection page)
        var productItemSkuElement = productItem.querySelector('.productitem--sku .sku');
        if (productItemSkuElement) {
            var sku = productItemSkuElement.textContent.trim().replace('#', '').trim();
            if (sku && sku.length > 0 && sku !== 'undefined') {
                return sku;
            }
        }
        
        // Try to get from .part-no span (catalog collection page)
        var partNoElement = productItem.querySelector('.part-no span');
        if (partNoElement) {
            var sku = partNoElement.textContent.trim().replace('#', '').trim();
            // Check if SKU is valid (not empty and not "undefined")
            if (sku && sku.length > 0 && sku !== 'undefined') {
                return sku;
            }
        }
        
        // If empty or missing, try to get from variant select options
        var selectElement = productItem.querySelector('select.product-variants option[selected]');
        if (selectElement) {
            var skuFromSelect = selectElement.getAttribute('data-sku');
            if (skuFromSelect && skuFromSelect !== 'undefined' && skuFromSelect.trim().length > 0) {
                return skuFromSelect.trim();
            }
        }
        
        // Also try first option with data-sku if no selected option
        if (!selectElement) {
            var firstOption = productItem.querySelector('select.product-variants option[data-sku]');
            if (firstOption) {
                var skuFromSelect = firstOption.getAttribute('data-sku');
                if (skuFromSelect && skuFromSelect !== 'undefined' && skuFromSelect.trim().length > 0) {
                    return skuFromSelect.trim();
                }
            }
        }
        
        return null;
    }
    
    // Function to get SKU via async fetch using product handle
    function fetchSKUViaHandle(productItem, callback) {
        var productLink = productItem.querySelector('a[href*="/products/"]');
        
        if (!productLink) {
            callback(null);
            return;
        }
        
        var href = productLink.getAttribute('href');
        var match = href.match(/\/products\/([^?#\/]+)/);
        if (match && match[1]) {
            var handle = match[1];
            
            fetchProductByHandle(handle, function(productData) {
                if (productData && productData.variants && productData.variants.length > 0) {
                    var sku = productData.variants[0].sku;
                    callback(sku);
                } else {
                    callback(null);
                }
            }, function() {
                callback(null);
            });
        } else {
            callback(null);
        }
    }
    
    // Helper function to process a single product
    function processSingleProduct(productItem, productSKU) {
        // Check if already has images - support catalog collection (list/grid), normal collection, and search
        var variantImageContainer = productItem.querySelector('.product-list-image, .product-grid-image, .productitem--image');
        if (!variantImageContainer) return;
        
        var variantPrimaryImg = variantImageContainer.querySelector('.grid-main-image, .productitem--image-primary, img.no_img, img');
        if (!variantPrimaryImg) return;
        
        var currentSrc = variantPrimaryImg.getAttribute('data-srcset') || variantPrimaryImg.src;
        
        // Process if has placeholder image
        if (isPlaceholderImage(currentSrc)) {
            if (isVariantSKU(productSKU)) {
                // Standard variant SKU with decimal (e.g., P4440.20)
                var baseSKU = getBaseSKU(productSKU);
                if (baseSKU) {
                    // Fetching images for variant SKU
                    fetchAndApplyBaseProductImages(productItem, baseSKU, productSKU);
                }
            } else {
                // Non-variant SKU with placeholder (like RB3245)
                // Fetching images for non-variant SKU
                fetchAndApplyBaseProductImages(productItem, productSKU, productSKU);
            }
        }
    }
    
    // Main function to process all variant products on the page
    function processVariantProducts() {
        // Try multiple selectors to find products - support catalog collection, normal collection, and search
        // Catalog: .product-list-item, .product-grid-item
        // Normal collection: .productgrid--item
        var allProducts = document.querySelectorAll('[data-productgrid-items] .product-list-item, .productgrid--items .product-list-item, [data-productgrid-items] .product-grid-item, .productgrid--items .product-grid-item, [data-productgrid-items] .productgrid--item, .productgrid--items .productgrid--item');
        
        // Log info message (not an error) - only on first meaningful run
        if (allProducts.length > 0 && stats.processed === 0) {
            console.info('%c[Collection Image Fallback] Processing ' + allProducts.length + ' products', 'color: #0066cc; font-weight: bold;');
            console.info('%cNote: Network 404 errors below are expected behavior while searching for product images. They are not actual errors.', 'color: #666; font-style: italic;');
        }
        
        var startCount = stats.successful;
        
        allProducts.forEach(function(productItem) {
            stats.processed++;
            
            // Try to get SKU synchronously first
            var productSKU = getSKUFromProductElementSync(productItem);
            
            if (productSKU) {
                // Process this product immediately
                processSingleProduct(productItem, productSKU);
            } else {
                // SKU not found in DOM, try to fetch it via handle
                fetchSKUViaHandle(productItem, function(fetchedSKU) {
                    if (fetchedSKU) {
                        // Fetched SKU successfully
                        processSingleProduct(productItem, fetchedSKU);
                    }
                });
            }
        });
        
        // Log summary after processing (only if we applied new images)
        setTimeout(function() {
            var newImages = stats.successful - startCount;
            if (newImages > 0) {
                console.info('%c[Collection Image Fallback] ✓ Applied ' + newImages + ' fallback images successfully', 'color: #00aa00; font-weight: bold;');
            }
        }, 2000); // Wait 2s for async operations to complete
        
        return allProducts.length;
    }
    
    // Run the fallback logic after products are loaded
    function initImageFallback() {
        // Run immediately
        processVariantProducts();
        
        // Also run after a delay to catch dynamically loaded products
        setTimeout(function() {
            processVariantProducts();
        }, 500);
        
        // Run after another delay for slower connections
        setTimeout(function() {
            processVariantProducts();
        }, 1500);
    }
    
    // Function to run fallback with retries (for AJAX callbacks)
    function runFallbackWithRetries() {
        // Run immediately
        processVariantProducts();
        
        // Retry after short delay
        setTimeout(processVariantProducts, 200);
        
        // Retry after longer delay for lazy-loaded images
        setTimeout(processVariantProducts, 800);
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initImageFallback);
    } else {
        initImageFallback();
    }
    
    // Expose a function with retry logic for AJAX product loading
    // This can be called after AJAX product loading
    window.catalogImageFallback = runFallbackWithRetries;
    
})();

